import { createQueryApi } from '@/shared/features/query/infra/adapters/rtk.adapter';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { AppDispatch } from '@/app/store/store';
import { executeQuery } from '@/shared/features/query/utils/queryUtils';
import { getApiBaseUrl } from '@/core/utils/apiUtils';
import type {
  BookProductSizerRequest,
  BookProductSizerResult,
  PincodeLookupResult,
  SendSizerOtpRequest,
  SendSizerOtpResult,
  SizerRepository,
  SizerState,
  VerifySizerOtpRequest,
  VerifySizerOtpResult,
} from '../../domain/interfaces/SizerRepository';

function toFormBody(data: Record<string, string | number>): string {
  return Object.entries(data)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');
}

function parseStatesResponse(response: unknown): SizerState[] {
  const dto = (response ?? {}) as {
    states?: Array<{ id: string; name: string }> | Record<string, string>;
  };
  const raw = dto.states;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((state) => ({ id: String(state.id), name: state.name }));
  }
  return Object.entries(raw).map(([id, name]) => ({ id: String(id), name: String(name) }));
}

function parsePincodeLookupResponse(response: unknown): PincodeLookupResult {
  const dto = (response ?? {}) as Record<string, unknown>;
  const idState = dto.id_state;
  return {
    isPincodeValid: Boolean(dto.isPincodeValid),
    hasError: Boolean(dto.hasError),
    city: String(dto.city ?? ''),
    statename: String(dto.statename ?? dto.stateName ?? dto.state ?? ''),
    id_state: idState != null && idState !== '' ? Number(idState) : undefined,
  };
}

function parseSendOtpResponse(response: unknown): SendSizerOtpResult {
  const dto = (response ?? {}) as Record<string, unknown>;
  return {
    hasError: Boolean(dto.hasError),
    errorMsg: String(dto.errorMsg ?? dto.message ?? ''),
    otpId: dto.otpId != null && dto.otpId !== '' ? String(dto.otpId) : '',
  };
}

function parseVerifyOtpResponse(response: unknown): VerifySizerOtpResult {
  const dto = (response ?? {}) as Record<string, unknown>;
  return {
    hasError: Boolean(dto.hasError),
    errorMsg: String(dto.errorMsg ?? dto.message ?? ''),
    otpId: dto.otpId != null && dto.otpId !== '' ? String(dto.otpId) : '',
  };
}

function parseBookProductSizerResponse(response: unknown): BookProductSizerResult {
  const dto = (response ?? {}) as Record<string, unknown>;
  const userCode =
    dto.userCode != null && dto.userCode !== '' ? String(dto.userCode) : undefined;
  return {
    hasError: Boolean(dto.hasError),
    message: String(dto.message ?? ''),
    isNewCustomerV2: Boolean(dto.isNewCustomerV2),
    userCode,
  };
}

const _sizerApi = createQueryApi({ clientId: 'sizerApi' });

const sizerRtkApi = _sizerApi.injectEndpoints({
  endpoints: (builder) => ({
    isSizerEnabled: builder.query<{ isSizerEnabled: boolean }, void>({
      query: () => ({
        url: `${getApiBaseUrl()}/productsizerhelp/is-sizer-enabled`,
        method: 'GET',
        withCredentials: true,
      }),
      transformResponse: (response: unknown) => {
        const dto = (response ?? {}) as { isSizerEnabled?: boolean };
        return { isSizerEnabled: Boolean(dto.isSizerEnabled) };
      },
    }),
    fetchStates: builder.query<SizerState[], { countryId?: number }>({
      query: ({ countryId = 101 }) => ({
        url: `${getApiBaseUrl()}/address/states?id_country=${countryId}`,
        method: 'GET',
        withCredentials: true,
      }),
      transformResponse: (response: unknown) => parseStatesResponse(response),
    }),
    lookupPincode: builder.query<PincodeLookupResult, { pincode: string }>({
      query: ({ pincode }) => ({
        url: `${getApiBaseUrl()}/address?pincode=${encodeURIComponent(pincode)}`,
        method: 'GET',
        withCredentials: true,
      }),
      transformResponse: (response: unknown) => parsePincodeLookupResponse(response),
    }),
    bookProductSizer: builder.mutation<BookProductSizerResult, BookProductSizerRequest>({
      query: (request) => ({
        url: `${getApiBaseUrl()}/productsizerhelp/bookproductsizer`,
        method: 'POST',
        data: toFormBody({
          email: request.email.toLowerCase(),
          mobile: request.mobile,
          name: request.name,
          address: request.address,
          street: request.street,
          locality: request.locality,
          landmark: request.landmark,
          city: request.city,
          state: request.state,
          postcode: request.postcode,
          customizationId: request.customizationId,
          otpId: request.otpId,
        }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      }),
      transformResponse: (response: unknown) => parseBookProductSizerResponse(response),
    }),
    sendOtp: builder.mutation<SendSizerOtpResult, SendSizerOtpRequest>({
      query: (request) => ({
        url: `${getApiBaseUrl()}/otp/send-otp`,
        method: 'POST',
        data: toFormBody({
          otpType: 'phoneverification',
          customerphone: request.mobile,
          email: request.email,
          otpId: request.otpId ?? '',
        }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      }),
      transformResponse: (response: unknown) => parseSendOtpResponse(response),
    }),
    verifyOtp: builder.mutation<VerifySizerOtpResult, VerifySizerOtpRequest>({
      query: (request) => ({
        url: `${getApiBaseUrl()}/otp/verify-otp`,
        method: 'POST',
        data: toFormBody({
          otpType: 'phoneverification',
          otpId: request.otpId,
          otpString: request.otpString,
        }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      }),
      transformResponse: (response: unknown) => parseVerifyOtpResponse(response),
    }),
  }),
});

export const sizerApiRedux = _sizerApi as unknown as ReduxQueryClient;

export class SizerAdapter implements SizerRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async isSizerEnabled(): Promise<boolean> {
    const result = await executeQuery<{ isSizerEnabled: boolean }>(
      sizerRtkApi.endpoints.isSizerEnabled.initiate(undefined),
      this.dispatch
    );
    return result.isSizerEnabled;
  }

  async fetchStates(countryId = 101): Promise<SizerState[]> {
    return executeQuery<SizerState[]>(
      sizerRtkApi.endpoints.fetchStates.initiate({ countryId }),
      this.dispatch
    );
  }

  async lookupPincode(pincode: string): Promise<PincodeLookupResult> {
    return executeQuery<PincodeLookupResult>(
      sizerRtkApi.endpoints.lookupPincode.initiate({ pincode }),
      this.dispatch
    );
  }

  async bookProductSizer(request: BookProductSizerRequest): Promise<BookProductSizerResult> {
    return executeQuery<BookProductSizerResult>(
      sizerRtkApi.endpoints.bookProductSizer.initiate(request),
      this.dispatch
    );
  }

  async sendOtp(request: SendSizerOtpRequest): Promise<SendSizerOtpResult> {
    return executeQuery<SendSizerOtpResult>(
      sizerRtkApi.endpoints.sendOtp.initiate(request),
      this.dispatch
    );
  }

  async verifyOtp(request: VerifySizerOtpRequest): Promise<VerifySizerOtpResult> {
    return executeQuery<VerifySizerOtpResult>(
      sizerRtkApi.endpoints.verifyOtp.initiate(request),
      this.dispatch
    );
  }
}
